import CryptoJS from 'crypto-js'
import { TextField } from '@mui/material'
import { useContext, useEffect, useState } from 'react'

import Dialog from '../common/dialog'
import { parseNumber } from './utils'
import { NegotiationType } from './lib'
import { useTranslation } from 'react-i18next'
import { countriesCurrencies } from '../../lib'
import { TradeOperation } from './trade-operation'
import { requestAccessToken } from '../../api/auth.api'
import { CustomerCompany, FormError } from '../../types'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { AuthContext, NotificationsContext } from '../../contexts'
import { processCustomerCompanyFunding } from '../../api/customers.api'
import { handleSyncDataToLocalDB, normalizeCustomerCompanyFundingData } from '../../utils'

export const AddNewCustomerCompanyFundingModal = ({
  handleCloseSession,
  customerCompanyData,
  handleOpenStateOfModal
}: {
  handleCloseSession: () => void
  customerCompanyData: CustomerCompany | null
  handleOpenStateOfModal: (openState: boolean) => void
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const { t } = useTranslation(['customer-companies-management', 'common'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const { isAuth } = useContext(AuthContext)
  const [errors, setErrors] = useState<FormError>({})
  // const [bankingPartner, setBankingPartner] = useState<string>('')
  const [fundingDescription, setFundingDescription] = useState<string>('')
  const [purchaseTradeResult, setPurchaseTradeResult] = useState<string>('')
  const [fundingPurchaseRate, setFundingPurchaseRate] = useState<string>('')
  const [formDataIsIncomplete, setFormDataIsIncomplete] = useState<boolean>(true)
  const [purchaseNetValueResult, setPurchaseNetValueResult] = useState<string>('')
  const [fundingPurchaseQuantity, setFundingPurchaseQuantity] = useState<string>('')
  const [fundingPurchaseNetValue, setFundingPurchaseNetValue] = useState<string>('')
  const [negotiationTypeAdditionalInfo, setNegotiationTypeAdditionalInfo] = useState<string>('')
  const [negotiationType, setNegotiationType] = useState<NegotiationType>(NegotiationType.ON_SPOT)
  const [dialogNewCustomerCompanyFundingIsLoading, setDialogNewCustomerCompanyFundingIsLoading] = useState<boolean>(false)

  useEffect(() => {
    setPurchaseTradeResult('0')
    setPurchaseNetValueResult('0')
  }, [])

  useEffect(() => {
    if (
      // bankingPartner &&
      fundingPurchaseRate &&
      fundingPurchaseQuantity &&
      fundingPurchaseNetValue &&
      (((negotiationType as NegotiationType) === NegotiationType.ON_SPOT && negotiationTypeAdditionalInfo) || ((negotiationType as NegotiationType) === NegotiationType.NEXT_DAY && negotiationTypeAdditionalInfo === ''))
    ) {
      setFormDataIsIncomplete(false)
    } else {
      setFormDataIsIncomplete(true)
    }

    handleCalculateNetValue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [/* bankingPartner, */ fundingPurchaseRate, fundingPurchaseQuantity, negotiationType, negotiationTypeAdditionalInfo, fundingPurchaseNetValue])

  useEffect(() => {
    const extractedNumberFundingPurchaseRate = fundingPurchaseRate.replaceAll('$', '').replaceAll(',', '').trim()
    const extractedNumberFundingPurchaseQuantity = fundingPurchaseQuantity.replaceAll('USD$', '').replaceAll(',', '').trim()

    if (
      !isNaN(Number(extractedNumberFundingPurchaseRate)) &&
      Number(extractedNumberFundingPurchaseRate) > 0 &&
      !isNaN(Number(extractedNumberFundingPurchaseQuantity)) &&
      Number(extractedNumberFundingPurchaseQuantity) > 0
    ) {
      const purchaseTradeResultToSet = Math.round(Number(extractedNumberFundingPurchaseRate) * Number(extractedNumberFundingPurchaseQuantity))
      setPurchaseTradeResult(parseNumber(parseFloat(String(purchaseTradeResultToSet)).toFixed(2)))
    } else {
      setPurchaseTradeResult('0')
    }
  }, [fundingPurchaseQuantity, fundingPurchaseRate])

  const handleGetFundingRateQuantity = (valueToSet: string) => {
    const extractedNumberOfValueToSet = valueToSet.replaceAll('USD$', '').replaceAll(',', '').trim()

    if (isNaN(Number(extractedNumberOfValueToSet)) && extractedNumberOfValueToSet.slice(-1) !== '.') return ''

    if (extractedNumberOfValueToSet === '') return ''

    return parseNumber(extractedNumberOfValueToSet)
  }

  const handleGetFundingRateValue = (valueToSet: string) => {
    const extracted = valueToSet.replaceAll('$', '').replaceAll(',', '').trim()

    const partialPattern = /^-?\d*(\.\d*)?$/
    if (!partialPattern.test(extracted)) return ''

    if (extracted === '' || extracted === '-' || extracted === '.' || extracted === '-.' || extracted.endsWith('.')) {
      return extracted
    }

    const numericValue = Number(extracted)
    if (isNaN(numericValue)) return ''

    return extracted
  }

  const handleProcessFunding = async () => {
    try {
      setErrors({})
      setDialogNewCustomerCompanyFundingIsLoading(true)

      const customerCompanyFundingToProcess = {
        // bankingPartner,
        negotiationType,
        fundingDescription,
        customerCompanyId: String(customerCompanyData?._id),
        referenceId: CryptoJS.lib.WordArray.random(8).toString(),
        negotiationAdditionalPercentage: Number(negotiationTypeAdditionalInfo),
        purchaseTradeResult: Number(purchaseTradeResult.replaceAll(',', '').trim()),
        purchaseNetValueResult: Number(purchaseNetValueResult.replaceAll(',', '').trim()),
        fundingPurchaseRate: Number(fundingPurchaseRate.replaceAll('$', '').replaceAll(',', '').trim()),
        expectedProfitCurrency: (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase(),
        fundingPurchaseQuantity: Number(fundingPurchaseQuantity.replaceAll('USD$', '').replaceAll(',', '').trim())
      }

      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const customerCompanyFundingProcessed = await processCustomerCompanyFunding(customerCompanyFundingToProcess, accessTokenData.data.accessToken)
      const customerCompanyFundingsToAdd = normalizeCustomerCompanyFundingData(customerCompanyFundingProcessed.data)
      await handleSyncDataToLocalDB('customer-company-fundings', customerCompanyFundingsToAdd)

      handleOpenStateOfModal(false)
      setDialogNewCustomerCompanyFundingIsLoading(false)
      updateNewNotificationStatus?.(true, 'customer-company-funding-created')
    } catch (error: any) {
      console.log(error, error?.response, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewCustomerCompanyFundingIsLoading(false)
    }
  }

  const handleCalculateNetValue = () => {
    if (fundingPurchaseRate && fundingPurchaseQuantity && (negotiationType as NegotiationType) === NegotiationType.ON_SPOT && negotiationTypeAdditionalInfo) {
      const negotiationTypeAdditionalInfoValue = Number(negotiationTypeAdditionalInfo)
      const fundingPurchaseRateValue = Number(fundingPurchaseRate.replaceAll('$', '').replaceAll(',', '').trim())
      const fundingPurchaseQuantityValue = Number(fundingPurchaseQuantity.replaceAll('USD$', '').replaceAll(',', '').trim())

      const purchaseNetValueWithoutAddValue = fundingPurchaseQuantityValue * fundingPurchaseRateValue
      const purchaseNetValueValueToAdd = (purchaseNetValueWithoutAddValue * negotiationTypeAdditionalInfoValue) / 100
      const purchaseNetValueToSet = parseNumber(Math.round(Number(purchaseNetValueWithoutAddValue - purchaseNetValueValueToAdd)).toFixed(2))

      const purchaseNetValueInUSDValueToAdd = (fundingPurchaseQuantityValue * negotiationTypeAdditionalInfoValue) / 100
      const purchaseNetValueInUSDToSet = parseNumber(Number(fundingPurchaseQuantityValue - purchaseNetValueInUSDValueToAdd).toFixed(2))

      setPurchaseNetValueResult(purchaseNetValueToSet)
      setFundingPurchaseNetValue(`USD$ ${purchaseNetValueInUSDToSet}`)
      return
    }

    if (fundingPurchaseRate && fundingPurchaseQuantity && (negotiationType as NegotiationType) === NegotiationType.NEXT_DAY) {
      const fundingPurchaseRateValue = Number(fundingPurchaseRate.replaceAll('$', '').replaceAll(',', '').trim())
      const fundingPurchaseQuantityValue = Number(fundingPurchaseQuantity.replaceAll('USD$', '').replaceAll(',', '').trim())

      const purchaseNetValueWithoutAddValue = Math.round(Number(fundingPurchaseQuantityValue * fundingPurchaseRateValue)).toFixed(2)
      const purchaseNetValueToSet = parseNumber(purchaseNetValueWithoutAddValue)
      const purchaseNetValueInUSDToSet = parseNumber(fundingPurchaseQuantityValue)

      setPurchaseNetValueResult(purchaseNetValueToSet)
      setFundingPurchaseNetValue(`USD$ ${purchaseNetValueInUSDToSet}`)

      return
    }

    setFundingPurchaseNetValue('')
    setPurchaseNetValueResult('0')
  }

  return (
    <Dialog
      errors={errors}
      maxHeight="870px"
      className="customer-funding-min-height"
      actionButtonDisabled={formDataIsIncomplete}
      dialogTitle={t('newClientFundingModal.title')}
      handleDialogAction={() => handleProcessFunding()}
      dialogIsLoading={dialogNewCustomerCompanyFundingIsLoading}
      actionButtonText={t('newClientFundingModal.actionButtonText')}
      handleDialogOpenState={(openState: boolean) => handleOpenStateOfModal(openState)}
    >
      <div className="company-customer-funding-modal-main-instruction">{t('newClientFundingModal.mainInstruction')}</div>
      <div className="company-customer-funding-content-container">
        {/* <div className="trade-module-container">
          <TradeOperation
            placeholder="'Select banking partner'"
            textFieldType="select"
            currencyOperation="COP/USD"
            tradeOperationValue={bankingPartner}
            title={t('newClientFundingModal.bankingPartner')}
            handleChangeTradeOperationValue={(valueToSet: string | NegotiationType) => setBankingPartner(String(valueToSet))}
          />
        </div> */}
        <div className="trade-module-container">
          <TradeOperation
            placeholder="0.00"
            textFieldType="currency"
            currencyOperation={`${(countriesCurrencies as { [key: string]: string })[selectedCountry]}/USD`}
            tradeOperationValue={fundingPurchaseRate}
            title={t('newClientFundingModal.purchaseRate')}
            handleClearTradeOperationValue={() => {
              if (fundingPurchaseRate.slice(-1) === '.') {
                setFundingPurchaseRate(fundingPurchaseRate.slice(0, -1))
              }
            }}
            handleChangeTradeOperationValue={(valueToSet: string | NegotiationType) => {
              const purchaseRateToSet = handleGetFundingRateValue(String(valueToSet))

              if (purchaseRateToSet === '') {
                setFundingPurchaseRate(purchaseRateToSet)
                return
              }

              setFundingPurchaseRate(`$ ${purchaseRateToSet}`)
            }}
          />
          <TradeOperation
            textFieldType="currency"
            placeholder="USD$ 00,00"
            tradeOperationValue={fundingPurchaseQuantity}
            currencyOperation={`= ${(countriesCurrencies as { [key: string]: string })[selectedCountry]}$ ${purchaseTradeResult}`}
            title={t('newClientFundingModal.purchaseQuantity')}
            handleClearTradeOperationValue={() => {
              if (fundingPurchaseQuantity.slice(-1) === '.') {
                setFundingPurchaseQuantity(fundingPurchaseQuantity.slice(0, -1))
              }
            }}
            handleChangeTradeOperationValue={(valueToSet: string | NegotiationType) => {
              const purchaseQuantityToSet = handleGetFundingRateQuantity(String(valueToSet))

              if (purchaseQuantityToSet === '') {
                setFundingPurchaseQuantity(purchaseQuantityToSet)
                return
              }

              setFundingPurchaseQuantity(`USD$ ${purchaseQuantityToSet}`)
            }}
          />
        </div>
        <div className="trade-module-container">
          <TradeOperation
            errors={errors}
            textFieldType="option"
            placeholder=""
            currencyOperation=""
            additionalInfoPlaceholder="Insert (%)"
            additionalInfoValue={negotiationTypeAdditionalInfo}
            tradeOperationValue={negotiationType as NegotiationType}
            optionsValues={[
              { value: NegotiationType.ON_SPOT, text: 'On spot' },
              { value: NegotiationType.NEXT_DAY, text: 'Next day' }
            ]}
            valuesOpenAdditionalInfo={[NegotiationType.ON_SPOT]}
            title={t('newClientFundingModal.negotiationType')}
            handleChangeTradeOperationValue={(valueToSet: string | NegotiationType) => {
              setNegotiationType(valueToSet as NegotiationType)
              setNegotiationTypeAdditionalInfo('')
            }}
            handleChangeAdditionalInfoValue={(valueToSet: string) => {
              setErrors({})
              if (isNaN(Number(valueToSet))) return

              if (!isNaN(Number(valueToSet)) && Number(valueToSet) > 100) {
                setErrors({
                  additionalInfoValue: t('newClientFundingModal.errors.percentage')
                })

                setNegotiationTypeAdditionalInfo('')
                return
              }

              setNegotiationTypeAdditionalInfo(valueToSet)
            }}
          />
        </div>
        <div className="trade-module-container">
          <TradeOperation
            disabled
            textFieldType="currency"
            placeholder="USD$ 00,00"
            title={t('newClientFundingModal.netValue')}
            tradeOperationValue={fundingPurchaseNetValue}
            currencyOperation={`= ${(countriesCurrencies as { [key: string]: string })[selectedCountry]}$ ${purchaseNetValueResult}`}
          />
        </div>
        <div className="trade-module-container">
          <div className="operation-container">
            <div className="operation-title">{t('newClientFundingModal.description')}</div>
            <div className="operation-calculation-container">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                variant="standard"
                sx={textFieldWhiteBg}
                value={fundingDescription || ''}
                placeholder={t('newClientFundingModal.decriptionPlaceholder')}
                onChange={(event) => setFundingDescription(event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
